import { useQuery } from '@tanstack/react-query'
import { api, MemoryCategory, StatsData } from '../api/client'
import { ErrorCard } from '../components/ui/ErrorCard'
import { RowSkeleton } from '../components/ui/Skeleton'

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({ value, label }: { value: number; label: string }) {
  return (
    <div className="bg-surface-1 border border-line-1 rounded-xl p-4 flex flex-col gap-1">
      <p className="text-2xl font-semibold text-ink leading-none">{value}</p>
      <p className="text-xs text-ink-2 leading-snug">{label}</p>
    </div>
  )
}

// ── SVG Bar Chart ─────────────────────────────────────────────────────────────

const BAR_W = 7
const BAR_GAP = 3
const CHART_H = 80

function BarChart({ data }: { data: StatsData['messages_by_day'] }) {
  const maxCount = Math.max(...data.map(d => d.count), 1)
  const totalW = data.length * (BAR_W + BAR_GAP) - BAR_GAP

  // Show only every 5th date label to avoid crowding
  const labelIndices = new Set([0, 6, 13, 20, 27, data.length - 1])

  return (
    <div className="w-full overflow-hidden">
      <svg
        viewBox={`0 0 ${totalW} ${CHART_H + 18}`}
        width="100%"
        preserveAspectRatio="none"
        style={{ display: 'block' }}
      >
        <defs>
          <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#5B7FFF" />
            <stop offset="100%" stopColor="#4A6CF7" />
          </linearGradient>
        </defs>
        {data.map((d, i) => {
          const x = i * (BAR_W + BAR_GAP)
          const barH = Math.max(2, Math.round((d.count / maxCount) * CHART_H))
          const y = CHART_H - barH

          // Date label: show day number for select indices
          const dayNum = new Date(d.date + 'T00:00:00').getDate()
          const showLabel = labelIndices.has(i)

          return (
            <g key={d.date}>
              <rect
                x={x}
                y={y}
                width={BAR_W}
                height={barH}
                rx={2}
                fill={d.count > 0 ? 'url(#barGrad)' : 'var(--color-bg-3)'}
                opacity={d.count > 0 ? 1 : 0.5}
              />
              {showLabel && (
                <text
                  x={x + BAR_W / 2}
                  y={CHART_H + 14}
                  textAnchor="middle"
                  fontSize={8}
                  fill="var(--color-text-2)"
                >
                  {dayNum}
                </text>
              )}
            </g>
          )
        })}
      </svg>
    </div>
  )
}

// ── SVG Pie Chart ─────────────────────────────────────────────────────────────

const CAT_COLORS: Record<MemoryCategory, string> = {
  personal: '#4A6CF7',
  work:     '#6E8EFF',
  business: '#3DB87A',
  misc:     '#5B7FFF80',
}

const CAT_LABELS: Record<MemoryCategory, string> = {
  personal: 'Личное',
  work:     'Работа',
  business: 'Бизнес',
  misc:     'Прочее',
}

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
}

function describeArc(cx: number, cy: number, r: number, startDeg: number, endDeg: number) {
  const start = polarToCartesian(cx, cy, r, endDeg)
  const end   = polarToCartesian(cx, cy, r, startDeg)
  const large = endDeg - startDeg > 180 ? 1 : 0
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${large} 0 ${end.x} ${end.y} Z`
}

function PieChart({ data }: { data: StatsData['memory_by_category'] }) {
  const total = Object.values(data).reduce((s, v) => s + v, 0)
  if (total === 0) {
    return (
      <p className="text-sm text-ink-2 text-center py-4">Фактов пока нет</p>
    )
  }

  const cx = 52, cy = 52, r = 46
  let currentDeg = 0
  const slices = (Object.keys(CAT_LABELS) as MemoryCategory[])
    .filter(k => data[k] > 0)
    .map(k => {
      const pct = data[k] / total
      const start = currentDeg
      currentDeg += pct * 360
      return { key: k, pct, startDeg: start, endDeg: currentDeg }
    })

  return (
    <div className="flex items-center gap-4">
      <svg width={104} height={104} viewBox="0 0 104 104" className="shrink-0">
        {slices.map(s => (
          <path
            key={s.key}
            d={describeArc(cx, cy, r, s.startDeg, s.endDeg)}
            fill={CAT_COLORS[s.key]}
          />
        ))}
      </svg>
      <div className="flex flex-col gap-2">
        {(Object.keys(CAT_LABELS) as MemoryCategory[]).map(k => (
          <div key={k} className="flex items-center gap-2">
            <span
              className="w-2.5 h-2.5 rounded-sm shrink-0"
              style={{ background: CAT_COLORS[k] }}
            />
            <span className="text-xs text-ink-2">
              {CAT_LABELS[k]}
            </span>
            <span className="text-xs font-medium text-ink ml-auto pl-2">
              {data[k] > 0
                ? `${Math.round((data[k] / total) * 100)}%`
                : '—'}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Stats page ────────────────────────────────────────────────────────────────

export function Stats() {
  const { data, isLoading, error, refetch } = useQuery<StatsData>({
    queryKey: ['stats'],
    queryFn: () => api.get('/api/stats'),
    staleTime: 60_000,
  })

  return (
    <div className="flex flex-col min-h-full page-enter">
      <div className="px-4 pt-5 pb-3">
        <p className="text-h1 text-ink">Статистика</p>
      </div>

      <div className="flex-1 px-4 pb-6 space-y-5">
        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => <RowSkeleton key={i} />)}
          </div>
        )}

        {error && <ErrorCard onRetry={refetch} />}

        {data && (
          <>
            {/* ── Stat cards ── */}
            <section>
              <p className="section-label">Всего</p>
              <div className="grid grid-cols-2 gap-3">
                <StatCard
                  value={data.tasks_completed_total}
                  label="Задач выполнено"
                />
                <StatCard
                  value={data.facts_total}
                  label="Фактов в памяти"
                />
                <StatCard
                  value={data.reminders_completed_total}
                  label="Напоминаний отправлено"
                />
                <StatCard
                  value={data.days_with_delo}
                  label="Дней с Delo"
                />
              </div>
            </section>

            {/* ── Activity bar chart ── */}
            <section>
              <p className="section-label">Активность — последние 30 дней</p>
              <div className="bg-surface-1 border border-line-1 rounded-xl p-4">
                {data.messages_by_day.every(d => d.count === 0) ? (
                  <p className="text-sm text-ink-2 text-center py-2">
                    Сообщений пока нет
                  </p>
                ) : (
                  <BarChart data={data.messages_by_day} />
                )}
              </div>
            </section>

            {/* ── Memory pie chart ── */}
            <section>
              <p className="section-label">Память по категориям</p>
              <div className="bg-surface-1 border border-line-1 rounded-xl p-4">
                <PieChart data={data.memory_by_category} />
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  )
}
